import {
  FeedAppState,
  PostUid,
  CommentUid,
  FeedUserUid,
  EditingPostUids,
  EditingCommentUids,
  SelectableFeedFilter,
  FeedfilterPopupState,
  SocialProfileState,
  FeedTagName,
} from 'interfaces/feed'
import { combineReducers } from 'redux'
import {
  TOGGLE_COMMENT_LIKE,
  ToggleCommentLike,
  TOGGLE_POST_LIKE,
  TogglePostLike,
  TOGGLE_AUTHOR_FOLLOWING,
  ToggleAuthorFollowing,
  TOGGLE_POST_FOLLOWING,
  TogglePostFollowing,
  TOGGLE_EDIT_POST,
  TOGGLE_EDIT_COMMENT,
  SELECT_FEED_FILTER,
  SHOW_POPUP_FEED_FILTER,
  HIDE_POPUP_FEED_FILTER,
  REGISTER_POPUP_FEED_FILTER_TIMEOUT,
  LOAD_FOLLOWED_POSTS_COUNT,
  GET_FILTERS_STATUS,
  GET_COMMENTS,
  GetComments,
  CHANGE_SOCIAL_PROFILE_STEP,
  ToggleTagFollowing,
  TOGGLE_TAG_FOLLOWING,
  RESET_FOLLOWED_USERS,
  SCHEDULE_RESET_FOLLOWED_USERS,
  RESET_FOLLOWED_TAGS,
  SCHEDULE_RESET_FOLLOWED_TAGS,
  TOGGLE_COMMENT_REPLIES,
  INIT_COMMENT_REPLY,
  COMMENT_REPLY_CANCEL,
} from 'actions/feedActions'
import { createLoadingSingleReducer, createLoadingReducer } from 'helpers/reducerHelpers'
import { Reducer } from 'interfaces'
import { omit } from 'lodash'
import { DELETE_RELATION } from 'actions/peergroupActions'
import { SET_SHOWN_SIDEBAR_PANE } from 'actions/sidebarActions'
import { PAGE_CHANGED } from 'actions/globalActions'
import { composes, composeLastFocus } from 'reducers/app/feedComposeReducer'
import { INSIGHT_CTA_CLICK } from 'actions/insightsActions'
import { isRequestAction, isSuccessAction } from 'helpers/actionsHelpers'
import { COMPOSE_SUBMIT } from 'actions/feedComposeActions'


const postCommentsLoadingIds = createLoadingReducer<PostUid, GetComments>(
  GET_COMMENTS,
  (a) => a.payload.params.postUid,
)

const likingCommentUids = createLoadingReducer<CommentUid, ToggleCommentLike>(
  TOGGLE_COMMENT_LIKE,
  (a) => a.payload.params.commentUid,
)

const likingPostUids = createLoadingReducer<PostUid, TogglePostLike>(
  TOGGLE_POST_LIKE,
  (a) => a.payload.params.postUid,
)

const authorFollowingLoadingIds = createLoadingReducer<FeedUserUid, ToggleAuthorFollowing>(
  TOGGLE_AUTHOR_FOLLOWING,
  (a) => a.payload.params.uid,
)

const postFollowingLoadingIds = createLoadingReducer<PostUid, TogglePostFollowing>(
  TOGGLE_POST_FOLLOWING,
  (a) => a.payload.params.postUid,
)

const tagFollowingLoadingIds = createLoadingReducer<FeedTagName, ToggleTagFollowing>(
  TOGGLE_TAG_FOLLOWING,
  (a) => a.payload.params.tag_name,
)

const editingPostsIds: Reducer<EditingPostUids> = (prevState = {}, action) => {
  switch (action.type) {
    case TOGGLE_EDIT_POST: {
      const { postUid, isEditing } = action.payload
      return isEditing
        ? { ...prevState, [postUid]: true }
        : omit(prevState, postUid)
    }

    case COMPOSE_SUBMIT: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { postUid, isEdit, parentCommentUid } = action.payload.response
      if (!postUid || !isEdit || !!parentCommentUid) {
        return prevState
      }

      return omit(prevState, postUid)
    }

    default:
      return prevState
  }
}

const editingCommentsIds: Reducer<EditingCommentUids> = (prevState = {}, action) => {
  switch (action.type) {
    case TOGGLE_EDIT_COMMENT: {
      const { uid: commentUid, isEditing } = action.payload
      return isEditing
        ? { ...prevState, [commentUid]: true }
        : omit(prevState, commentUid)
    }

    case COMPOSE_SUBMIT: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { commentUid } = action.payload.response
      if (!commentUid) {
        return prevState
      }

      return omit(prevState, commentUid)
    }

    default:
      return prevState
  }
}

const replyCommentsIds: Reducer<EditingCommentUids> = (prevState = {}, action) => {
  switch (action.type) {
    case INIT_COMMENT_REPLY: {
      const { commentUid } = action.payload
      return {
        ...prevState,
        [commentUid]: true,
      }
    }

    case COMMENT_REPLY_CANCEL: {
      const { commentUid } = action.payload

      return omit(prevState, commentUid)
    }

    case COMPOSE_SUBMIT: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { commentUid } = action.payload.response
      if (!commentUid) {
        return prevState
      }

      return omit(prevState, commentUid)
    }
  }
  return prevState
}

const hiddenRepliesCommentsIds: Reducer<EditingCommentUids> = (prevState = {}, action) => {
  switch (action.type) {
    case TOGGLE_COMMENT_REPLIES: {
      const { commentUid, toShow } = action.payload
      return toShow
        ? { ...prevState, [commentUid]: true }
        : omit(prevState, commentUid)
    }
  }
  return prevState
}

const userSelectedFilter: Reducer<SelectableFeedFilter | null> = (prevState = null, action) => {
  switch (action.type) {
    case SELECT_FEED_FILTER:
      return action.payload

    case DELETE_RELATION: {
      if (
        prevState
        && prevState.type === 'peergroup'
        && prevState.relationId === action.payload
      ) {
        return null
      }

      return prevState
    }

    default:
      return prevState
  }
}

const FILTER_POPUP_INITIAL_STATE: FeedfilterPopupState = {
  shownFilter: null,
  rect: null,
  hideTimeoutId: null,
}

const filterPopup: Reducer<FeedfilterPopupState> = (prevState = FILTER_POPUP_INITIAL_STATE, action) => {
  switch (action.type) {
    case SHOW_POPUP_FEED_FILTER:
      return {
        ...prevState,
        shownFilter: action.payload ? action.payload.filter : null,
        rect: action.payload ? action.payload.rect : null,
      }

    case PAGE_CHANGED:
    case SELECT_FEED_FILTER:
    case SET_SHOWN_SIDEBAR_PANE:
    case HIDE_POPUP_FEED_FILTER:
      return {
        ...prevState,
        shownFilter: null,
        rect: null,
      }

    case REGISTER_POPUP_FEED_FILTER_TIMEOUT:
      return {
        ...prevState,
        hideTimeoutId: action.payload,
      }

    case DELETE_RELATION: {
      if (
        prevState.shownFilter
        && prevState.shownFilter.type === 'peergroup'
        && prevState.shownFilter.relationId === action.payload
      ) {
        return {
          ...prevState,
          shownFilter: null,
          rect: null,
        }
      }

      return prevState
    }

    default:
      return prevState
  }
}

const shouldResetFollowedPeople: Reducer<boolean> = (prevState = false, action) => {
  switch (action.type) {
    case RESET_FOLLOWED_USERS:
      return false

    case SCHEDULE_RESET_FOLLOWED_USERS:
      return true

    default:
      return prevState
  }
}

const shouldResetFollowedTags: Reducer<boolean> = (prevState = false, action) => {
  switch (action.type) {
    case RESET_FOLLOWED_TAGS:
      return false

    case SCHEDULE_RESET_FOLLOWED_TAGS:
      return true

    default:
      return prevState
  }
}

const SOCIAL_PROFILE_DEFAULT_STATE: SocialProfileState = {
  step: null,
}
const socialProfile: Reducer<SocialProfileState> = (prevState = SOCIAL_PROFILE_DEFAULT_STATE, action) => {
  if (action.type === CHANGE_SOCIAL_PROFILE_STEP) {
    const { step, focusComposeId } = action.payload
    const nextFocusComposeId = step
      ? (focusComposeId ? focusComposeId : prevState.focusComposeId)
      : undefined

    return {
      ...prevState,
      step,
      focusComposeId: nextFocusComposeId,
    }
  }

  return prevState
}

const isFollowedPostsLoading = createLoadingSingleReducer(
  LOAD_FOLLOWED_POSTS_COUNT,
)

const isFiltersStatusLoading = createLoadingSingleReducer(
  GET_FILTERS_STATUS,
)

const targetCommentReducer: Reducer<FeedAppState['targetComment']> = (prevState = null, action) => {
  switch (action.type) {
    case INSIGHT_CTA_CLICK: {
      return action.payload.type === 'view_comment'
        ? action.payload.cta
        : prevState
    }

    case GET_COMMENTS: {
      if (
        isRequestAction(action)
        && prevState
        && action.payload.params.postUid === prevState.post_uid
      ) {
        return null
      }

      return prevState
    }

    default:
      return prevState
  }
}


export default combineReducers<FeedAppState>({
  postCommentsLoadingIds,
  likingCommentUids,
  likingPostUids,
  authorFollowingLoadingIds,
  postFollowingLoadingIds,
  tagFollowingLoadingIds,
  editingPostsIds,
  editingCommentsIds,
  replyCommentsIds,
  userSelectedFilter,
  filterPopup,
  composes,
  isFollowedPostsLoading,
  composeLastFocus,
  isFiltersStatusLoading,
  socialProfile,
  shouldResetFollowedPeople,
  shouldResetFollowedTags,
  targetComment: targetCommentReducer,
  hiddenRepliesCommentsIds,
})

